/**
 * Tokens CSS extendidos (no incluidos en FullThemeTokens).
 * Sincronizados con `src/theme-variables.scss`.
 * Experience puede sobreescribirlos vía `cssVarOverrides` en AppThemeService.
 */
export const DEFAULT_EXTENDED_CSS_VARS: Record<string, string> = {
    '--color-border-subtle': '#d7d0e8',
    '--color-surface-purple': '#f4f0fb',
    '--color-surface-muted': '#f5f5f5',
    '--color-surface-alt': '#e6e6e6',
    '--color-surface-neutral': '#e3e3e3',
    '--color-material-error': '#f44336',
    '--color-chip-label': '#4e4d4d',
    '--calendar-series-1': '#2d00f7',
    '--calendar-series-2': '#9b5de5',
    '--calendar-series-3': '#d100d1',
    '--calendar-series-4': '#ff7a00',
    '--calendar-series-5': '#f2ad13',
    '--calendar-series-6': '#f2545b',
    '--calendar-series-7': '#297045',
    '--calendar-series-8': '#f78e69',
    '--calendar-series-9': '#41309e',
    '--calendar-series-10': '#334151',
    '--calendar-series-11': '#944394',
    '--calendar-series-12': '#00bb94',
    '--schedule-module-header-bg': '#ffe8cc',
    '--schedule-module-header-color': '#ff8a00',
    '--schedule-practice-header-bg': '#f2eff8',
    '--schedule-practice-header-color': '#8f77c2',
    '--schedule-success-header-bg': '#e8f5e9',
    '--schedule-success-header-color': '#2e7d32',
    '--color-text-muted': '#737373',
    '--color-border-rose': '#f7efef',
    '--color-border-primary-light': '#dad2ed',
    '--color-chip-patient-bg': '#e0f7f9',
    '--color-chip-patient-text': '#5fb4b4',
    /* Soft brand chips (AUTOCUIDADO): defaults = previous lightest/lighter combo */
    '--color-chip-soft-bg': '#f3eef9',
    '--color-chip-soft-text': '#c7bbe0',
    /* Form labels / field icons — defaults match current Material+primary look */
    '--color-form-label': 'rgba(0, 0, 0, 0.6)',
    '--color-form-label-focus': '#441d99',
    '--color-form-field-icon': '#441d99',
    /* Filter switcheable chips (Activo/Inactivo… + agenda) */
    '--color-switcheable-chip-active-bg': '#c7bbe0',
    '--color-switcheable-chip-control-bg': '#1b0c3d',
    '--color-critical': '#d30000',
    '--color-warning-accent': '#d99e14',
    '--color-surface-rose': '#f4f0f0',
    '--color-surface-grey': '#e9e9e9',
    '--color-surface-indicator': '#e4e2e6',
    /* Table / provisional indicators — defaults match pre-existing look; SPORT overrides */
    '--table-header-bg': '#c7bbe0',
    '--table-header-color': '#1b0c3d',
    '--table-cell-border': '#e0e0e0',
    '--color-indicator-card-stroke': '#f3eef9',
    '--color-provisional-practices-bg': 'transparent',
    '--color-tab-active': '#441d99',
    '--color-loading-bg': '#1d1f1d',
    '--color-loading-text': '#d9d6d1',
    '--color-loading-accent': '#5463ba',
    '--color-scrollbar-thumb': '#6f6f72',
    '--color-scrollbar-thumb-hover': '#5b5b5e',
    '--color-track-inactive': '#d8d8d8',
    '--color-filter-chip-active-bg': '#cfeeff',
    '--color-filter-chip-active-text': '#0a6091',
    '--color-filter-chip-inactive-bg': '#505050',
    '--color-map-alert': '#a30202',
    '--color-map-primary-emphasis': '#5427b4',
    '--color-expiring-soon-text': '#4d3700',
    '--color-multiselect-disabled-bg': '#eceeef',
    '--color-multiselect-panel-bg': '#f2f2f2',
    '--color-border-strong': '#cccccc',
    '--color-grey-999': '#999999',
    '--color-disabled-surface': '#e5e5e5',
    '--color-evolution-btn': '#7e9cb5',
    '--color-login-label': '#694aad',
    '--color-login-restore': '#1b0c3d',
    '--color-heading': '#1b0c3d',
    '--color-chrome': '#441d99',
    /* Idle sidenav icons — text grey in default; navy via SPORT rubric theme */
    '--color-sidenav-icon': '#3b3b3b',
    '--text-transform-heading': 'none',
    '--font-family-display-extrabold': "'MPlusRoundedExtrabold', var(--font-family-primary)",
    '--color-primary-subtle-border': 'rgba(107, 90, 214, 0.25)',
    '--color-primary-alpha-20': 'rgba(94, 11, 220, 0.2)',
    '--shadow-filter-card': '0 1px 2px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(60, 33, 148, 0.08)',
    '--gradient-primary-button': 'linear-gradient(180deg, var(--color-chrome) 0%, var(--color-chrome) 100%)',
    '--overlay-scrim': 'rgba(0, 0, 0, 0.15)',
    '--overlay-divider-inset': 'rgba(0, 0, 0, 0.02)',
    '--overlay-hover': 'rgba(0, 0, 0, 0.04)',
    '--overlay-white-93': 'rgba(255, 255, 255, 0.93)',
    '--overlay-scrim-light': 'rgba(0, 0, 0, 0.1)',
    '--overlay-scrim-medium': 'rgba(0, 0, 0, 0.3)',
    '--overlay-scrim-strong': 'rgba(0, 0, 0, 0.5)',
    '--shadow-elevation-tasks': '0 4px 8px rgba(0, 0, 0, 0.1)',
    '--shadow-elevation-tasks-strong': '0 4px 8px rgba(0, 0, 0, 0.3)',
    '--shadow-case-creation': '0px 1px 5px rgba(0, 0, 0, 0.2), 0px 3px 4px rgba(0, 0, 0, 0.12), 0px 2px 4px rgba(0, 0, 0, 0.14)',
    '--gradient-surface-primary-93':
        'linear-gradient(0deg, var(--overlay-white-93) 0%, var(--overlay-white-93) 100%), var(--color-primary)',
    '--overlay-divider': 'rgba(0, 0, 0, 0.12)',
    '--overlay-divider-light': 'rgba(0, 0, 0, 0.08)',
    '--overlay-divider-subtle': 'rgba(0, 0, 0, 0.06)',
    '--overlay-divider-faint': 'rgba(0, 0, 0, 0.05)',
    '--overlay-border-disabled': 'rgba(0, 0, 0, 0.26)',
    '--overlay-white-30': 'rgba(255, 255, 255, 0.3)',
    '--overlay-white-50': 'rgba(255, 255, 255, 0.5)',
    '--overlay-white-92': 'rgba(255, 255, 255, 0.92)',
    '--overlay-white-95': 'rgba(255, 255, 255, 0.95)',
    '--overlay-text-primary': 'rgba(0, 0, 0, 0.87)',
    '--overlay-text-secondary': 'rgba(0, 0, 0, 0.6)',
    '--overlay-text-muted': 'rgba(128, 128, 128, 0.603)',
    '--shadow-elevation-1': '0 2px 4px rgba(0, 0, 0, 0.1)',
    '--shadow-elevation-2':
        '0px 1px 3px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 1px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.12)',
    '--shadow-elevation-3':
        '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
    '--shadow-elevation-4':
        '1px 1px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
    '--shadow-elevation-5': '0px 1px 4px rgba(0, 0, 0, 0.25)',
    '--shadow-elevation-6': '0 2px 4px rgba(0, 0, 0, 0.5)',
    '--shadow-elevation-7': '0 1px 1px rgba(0, 0, 0, 0.19), 0 1px 1px rgba(0, 0, 0, 0.23)',
    '--shadow-elevation-8': '0 1px 2px rgba(0, 0, 0, 0.04)',
    '--shadow-elevation-9':
        '0 2px 4px rgba(0, 0, 0, 0.08), 0 8px 20px rgba(16, 24, 40, 0.12), 0 28px 32px -16px rgba(16, 24, 40, 0.16)',
    '--shadow-elevation-10': '0 1px 3px rgba(16, 24, 40, 0.08)',
    '--shadow-elevation-11': '0 15px 15px -10px rgba(0, 0, 0, 0.1)',
    '--shadow-elevation-12': '1px 1px 10px rgba(105, 105, 105, 0.2)',
    '--shadow-chip': '0 2px 4px rgba(0, 0, 0, 0.1)',
    '--shadow-switcheable-chip': '0 2px 4px rgba(0, 0, 0, 0.1)',
    '--shadow-map-focus': '0 0 0 3px rgba(108, 99, 255, 0.6)',
    '--shadow-map-panel': '-2px 2px 4px 0 rgba(188, 188, 188, 0.5)',
    '--shadow-sidenav': '2px 0 4px rgba(181, 181, 181, 0.25)',
    '--shadow-sidenav-toggle': '-2px 2px 4px rgba(188, 188, 188, 0.25)',
    '--abm-card-border': 'rgba(16, 24, 40, 0.06)',
    '--abm-card-shadow':
        '0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 12px rgba(16, 24, 40, 0.08), 0 20px 24px -12px rgba(16, 24, 40, 0.12)',
    '--abm-card-shadow-hover':
        '0 2px 4px rgba(16, 24, 40, 0.08), 0 8px 20px rgba(16, 24, 40, 0.12), 0 28px 32px -16px rgba(16, 24, 40, 0.16)',
    /* Component defaults (Experience components.* → COMPONENT_*) */
    '--component-card-radius': '16px',
    '--component-card-border': 'rgba(16, 24, 40, 0.06)',
    '--component-card-shadow':
        '0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 12px rgba(16, 24, 40, 0.08), 0 20px 24px -12px rgba(16, 24, 40, 0.12)',
    '--component-card-shadow-hover':
        '0 2px 4px rgba(16, 24, 40, 0.08), 0 8px 20px rgba(16, 24, 40, 0.12), 0 28px 32px -16px rgba(16, 24, 40, 0.16)',
    '--component-card-background': '#ffffff',
    '--component-card-flex-grow': '1',
    '--component-card-flex-shrink': '1',
    '--component-card-flex-basis': '30%',
    '--component-card-max-width': '30%',
    '--component-card-min-height': 'unset',
    '--component-card-max-height': 'none',
    '--component-card-padding': 'unset',
    '--component-table-header-bg': '#c7bbe0',
    '--component-table-header-color': '#1b0c3d',
    '--component-table-cell-border': '#e0e0e0',
    '--component-table-row-hover-bg': '#f5f5f5',
    '--component-table-row-zebra-bg': 'transparent',
    '--component-table-header-font-size': '12px',
    '--component-table-cell-font-size': '11px',
    '--component-icon-chrome': '#441d99',
    '--component-icon-action': '#441d99',
    '--component-icon-muted': '#9e9e9e',
    '--component-icon-danger': '#d02700',
    '--component-typography-font-size-headline-1': '28px',
    '--component-typography-font-size-headline-2': '22px',
    '--component-typography-font-size-body': '14px',
};
